// src/dateRange.js
// Pure, tested date-range helpers (no React, no Date parsing of strings).
//
// Dates are plain "YYYY-MM-DD" text and are compared as text, so a time zone can never move an item
// across a range edge. Today's date is read with the local getFullYear/getMonth/getDate (never
// toISOString, which is UTC).
//
// Money rules are the same as src/aiCharts.js (the charts and the AI use the same events):
//   revenue = invoices + manual revenue
//   costs   = purchase lines tagged to the job + manual expenses + labor
// A purchase line the owner's rules count as labor (line.isLabor) is labor; every other cost is materials,
// so materialCost + laborCost = costs exactly.
// Sums are whole cents, turned into dollars only at the end.
// Money with no date cannot be placed in a range. It is left out and reported (undatedLeftOut).

import { toWholeCents } from "./money";
import { jobEvents, dayOf, marginPercent } from "./aiCharts";

const YMD_RE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const pad = (n) => String(n).padStart(2, "0");

// Local "YYYY-MM-DD" for a Date.
export function localYMD(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// The start and end of a date range as "YYYY-MM-DD" text, both inclusive. A missing end (null) means
// "no upper limit". Returns null when there is no filter at all (all time, or a custom range with
// neither date filled in).
// rangeKey: "all" | "mtd" | "qtd" | "ytd" | "prior_year" | "custom"; today: a Date.
export function rangeBounds(rangeKey, customStart, customEnd, today) {
  const y = today.getFullYear();
  const m = today.getMonth(); // 0-indexed
  switch (rangeKey) {
    case "mtd": return { start: `${y}-${pad(m + 1)}-01`, end: null };
    case "qtd": return { start: `${y}-${pad(Math.floor(m / 3) * 3 + 1)}-01`, end: null };
    case "ytd": return { start: `${y}-01-01`, end: null };
    case "prior_year": return { start: `${y - 1}-01-01`, end: `${y - 1}-12-31` };
    case "custom": {
      const start = typeof customStart === "string" && YMD_RE.test(customStart) ? customStart : null;
      const end = typeof customEnd === "string" && YMD_RE.test(customEnd) ? customEnd : null;
      return start || end ? { start, end } : null;
    }
    default: return null;
  }
}

// True when the date text falls on or between start and end (either may be null). Text with no
// readable date is never in range.
export function inRangeYMD(dateText, start, end) {
  const d = dayOf(dateText);
  return d !== null && (!start || d >= start) && (!end || d <= end);
}

// Narrow one job to a date range. Returns the job with these recomputed from the dated items inside
// the range (start and end inclusive, "YYYY-MM-DD", either may be null):
//   revenue, costs, materialCost, laborCost, profit, marginPct (a string like "12.5", "0.0" with no
//   revenue, same as the job summary), outstanding, costByVendor,
//   and the lists invoices, purchases, manualRevenue, manualExpenses, laborEntries (items in range).
// Plus undatedLeftOut: { costCents, revCents } for money that has no date, which can't be placed.
// Purchases count only their lines tagged to this job.
export function filterJobToRange(job, start, end) {
  let revC = 0, matC = 0, laborC = 0, undatedCostCents = 0, undatedRevCents = 0;
  for (const e of jobEvents(job)) {
    if (e.day === null) {
      if (e.kind === "cost") undatedCostCents += e.cents; else undatedRevCents += e.cents;
      continue;
    }
    if ((start && e.day < start) || (end && e.day > end)) continue;
    if (e.kind === "rev") revC += e.cents;
    else if (e.src === "labor" || e.src === "purchaseLabor" || e.src === "manualExpLabor") laborC += e.cents;
    else matC += e.cents;
  }
  const costC = matC + laborC;
  const keep = (list, field) => (list || []).filter((x) => inRangeYMD(x[field], start, end));
  const invoices = keep(job.invoices, "TxnDate");
  const purchases = keep(job.purchases, "TxnDate");
  const manualExpenses = keep(job.manualExpenses, "expenseDate");

  const costByVendorCents = {};
  const addVendor = (name, amount) => {
    if (!name) return;
    costByVendorCents[name] = (costByVendorCents[name] || 0) + toWholeCents(amount);
  };
  purchases.forEach((p) => (p.Line || []).forEach((l) => {
    if (l && l.AccountBasedExpenseLineDetail?.CustomerRef?.value === job.id) addVendor(p.EntityRef?.name, l.Amount || 0);
  }));
  manualExpenses.forEach((x) => addVendor(x.vendor, x.amount || 0));
  const costByVendor = {};
  for (const k of Object.keys(costByVendorCents)) costByVendor[k] = costByVendorCents[k] / 100;

  const margin = marginPercent(revC - costC, revC);
  return {
    ...job,
    invoices, purchases, manualExpenses,
    manualRevenue: keep(job.manualRevenue, "revenueDate"),
    laborEntries: keep(job.laborEntries, "workDate"),
    revenue: revC / 100,
    costs: costC / 100,
    materialCost: matC / 100,
    laborCost: laborC / 100,
    profit: (revC - costC) / 100,
    marginPct: margin === null ? "0.0" : margin.toFixed(1),
    outstanding: invoices.reduce((s, i) => s + toWholeCents(i.Balance || 0), 0) / 100,
    costByVendor,
    undatedLeftOut: { costCents: undatedCostCents, revCents: undatedRevCents },
  };
}

// The jobs narrowed to the range. Jobs with nothing in the range are dropped. Same list shape as the
// job summaries. rangeKey "all" (or an empty custom range) returns the jobs untouched.
export function filterJobsToRange(jobs, rangeKey, customStart, customEnd, today) {
  const b = rangeBounds(rangeKey, customStart, customEnd, today);
  if (!b) return jobs;
  return jobs.map((j) => filterJobToRange(j, b.start, b.end)).filter((j) => j.revenue !== 0 || j.costs !== 0);
}

// Undated money (whole cents) a range leaves out, counted over ALL the jobs, including jobs that are
// dropped from the ranged list because nothing dated fell in the range. Zero when there is no range.
export function undatedForRange(jobs, rangeKey, customStart, customEnd, today) {
  const b = rangeBounds(rangeKey, customStart, customEnd, today);
  let costCents = 0, revCents = 0;
  if (!b) return { costCents, revCents };
  for (const j of jobs || []) {
    const u = filterJobToRange(j, b.start, b.end).undatedLeftOut;
    costCents += u.costCents; revCents += u.revCents;
  }
  return { costCents, revCents };
}

const daysIn = (y, m1) => new Date(Date.UTC(y, m1, 0)).getUTCDate(); // m1 is 1-12
const ymd = (y, m1, d) => `${y}-${pad(m1)}-${pad(d)}`;

// The WHOLE period before, for "vs prior period" comparisons (owner decision: this month so far is
// compared with all of last month, not just the same number of days). Calendar arithmetic on the
// local year/month of today (never milliseconds or UTC). The current period keeps everything dated in it.
//   mtd: all of last month.   qtd: all of last quarter.   ytd: all of last year.
//   prior_year: all of the year before last.
// Returns { start, end } as "YYYY-MM-DD", or null for any other range.
export function priorPeriodBounds(rangeKey, today) {
  const y = today.getFullYear();
  const m1 = today.getMonth() + 1;
  if (rangeKey === "mtd") {
    const pm = m1 === 1 ? 12 : m1 - 1;
    const py = m1 === 1 ? y - 1 : y;
    return { start: ymd(py, pm, 1), end: ymd(py, pm, daysIn(py, pm)) };
  }
  if (rangeKey === "qtd") {
    const curQStart = m1 - ((m1 - 1) % 3);
    const pStart = curQStart === 1 ? 10 : curQStart - 3;
    const py = curQStart === 1 ? y - 1 : y;
    return { start: ymd(py, pStart, 1), end: ymd(py, pStart + 2, daysIn(py, pStart + 2)) };
  }
  if (rangeKey === "ytd") return { start: ymd(y - 1, 1, 1), end: ymd(y - 1, 12, 31) };
  if (rangeKey === "prior_year") return { start: ymd(y - 2, 1, 1), end: ymd(y - 2, 12, 31) };
  return null;
}

// A date from a spreadsheet cell or typed text -> "YYYY-MM-DD", or null when it can't be read. Never
// goes through UTC, so the day can't shift. Accepts: a Date (local year/month/day), "YYYY-MM-DD" (also
// with a time after a T), "M/D/YYYY", "M/D/YY", "YYYY/M/D", an Excel serial number (a number), and
// anything else `new Date(text)` can read (read with local year/month/day).
export function toISODate(val) {
  if (!val && val !== 0) return null;
  if (val instanceof Date) return isNaN(val) ? null : localYMD(val);
  if (typeof val === "number") {
    if (!Number.isFinite(val) || val < 1 || val >= 2958466) return null;
    // Excel counts days from 1899-12-30 (it wrongly treats 1900 as a leap year, which only matters before Mar 1900).
    const dt = new Date(Date.UTC(1899, 11, 30) + Math.floor(val) * 86400000);
    return ymd(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
  }
  const s = String(val).trim();
  if (!s) return null;
  const valid = (yy, mm, dd) => mm >= 1 && mm <= 12 && dd >= 1 && dd <= daysIn(yy, mm) ? ymd(yy, mm, dd) : null;
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:T|$)/);
  if (m) return valid(+m[1], +m[2], +m[3]);
  m = s.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);
  if (m) return valid(+m[1], +m[2], +m[3]);
  m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4}|\d{2})$/);
  if (m) {
    const yy = m[3].length === 2 ? (+m[3] < 50 ? 2000 : 1900) + +m[3] : +m[3];
    return valid(yy, +m[1], +m[2]);
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : localYMD(d);
}
